import {AlertTriangle, CalendarX2, Clock, Unlink} from 'lucide-react';
import {cn} from '@chekinapp/ui';

type ErrorSceneVariant = 'link' | 'rate' | 'crash' | 'gone';

const halo = (token: string, opacity: number) =>
  `radial-gradient(circle, color-mix(in srgb, var(${token}) ${
    opacity * 100
  }%, transparent), transparent 70%)`;

const VARIANTS: Record<
  ErrorSceneVariant,
  {ring: string; badgeClassName: string; Icon: typeof Unlink}
> = {
  link: {
    ring: halo('--chekin-blue-500', 0.5),
    badgeClassName:
      'bg-[var(--chekin-blue-tint-info)] text-[color:var(--chekin-blue-500)]',
    Icon: Unlink,
  },
  rate: {
    ring: halo('--chekin-amber-500', 0.45),
    badgeClassName: 'bg-[var(--chekin-amber-50)] text-[color:var(--chekin-amber-700)]',
    Icon: Clock,
  },
  crash: {
    ring: halo('--chekin-red-soft', 0.42),
    badgeClassName: 'bg-[var(--chekin-red-100)] text-[color:var(--chekin-red-500)]',
    Icon: AlertTriangle,
  },
  gone: {
    ring: halo('--chekin-neutral-400', 0.42),
    badgeClassName:
      'bg-[var(--chekin-neutral-75)] text-[color:var(--chekin-text-secondary)]',
    Icon: CalendarX2,
  },
};

type ErrorArtProps = {
  variant: ErrorSceneVariant;
  className?: string;
};

function ErrorArt({variant, className}: Readonly<ErrorArtProps>) {
  const {ring, badgeClassName, Icon} = VARIANTS[variant];

  return (
    <div className={cn('relative grid size-[132px] place-items-center', className)}>
      <span
        aria-hidden="true"
        className="absolute inset-0 animate-err-breathe opacity-[0.55] [--err-ring-opacity:0.55] motion-reduce:animate-none"
        style={{background: ring}}
      />
      <span
        aria-hidden="true"
        className="absolute inset-4 animate-err-breathe opacity-40 [--err-ring-opacity:0.4] [animation-delay:0.4s] motion-reduce:animate-none"
        style={{background: ring}}
      />
      <span
        className={cn(
          'relative grid size-[86px] place-items-center rounded-full',
          badgeClassName,
        )}
      >
        <Icon size={40} strokeWidth={1.9} aria-hidden="true" />
      </span>
    </div>
  );
}

export {ErrorArt};
export type {ErrorSceneVariant};
